'use client';

import { useTranslations } from 'next-intl';
import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Table, TableHeader } from '@/components/ui';
import { InProgressMatchCard } from './InProgressMatchCard';
import { InProgressMatchRow } from './InProgressMatchRow';
import type { MatchWithRatingsV2 } from './types';
import { MatchCardsSkeleton, TableSkeleton } from '@/components/ui/skeletons';

interface InProgressMatchGridProps {
  matches: MatchWithRatingsV2[];
  loading?: boolean;
  emptyMessage?: string;
  title?: string;
  locale: string;
  onMatchDelete?: (matchId: string) => void;
}

export function InProgressMatchGrid({
  matches,
  loading = false,
  emptyMessage,
  title,
  onMatchDelete,
}: InProgressMatchGridProps) {
  const tMatches = useTranslations('Matches');
  const [isMobile, setIsMobile] = useState(false);

  const defaultEmptyMessage = tMatches('noMatches');
  const defaultTitle = tMatches('inProgress');

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return (
    <Card className="border-orange-500/20">
      <CardHeader>
        <CardTitle className="text-orange-600 dark:text-orange-400">
          {title || defaultTitle}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          isMobile ? (
            <MatchCardsSkeleton count={2} className="p-4" />
          ) : (
            <TableSkeleton cols={5} rows={2} />
          )
        ) : matches.length === 0 ? (
          <div className="text-muted px-4 py-3 text-sm">{emptyMessage || defaultEmptyMessage}</div>
        ) : isMobile ? (
          // Mobile: Card layout
          <div className="space-y-3 p-4">
            {matches.map((match) => (
              <InProgressMatchCard key={match.id} match={match} onDelete={onMatchDelete} />
            ))}
          </div>
        ) : (
          // Desktop: Table layout
          <div className="overflow-x-auto">
            <Table>
              <thead>
                <tr>
                  <TableHeader className="w-36">{tMatches('columns.date')}</TableHeader>
                  <TableHeader>{tMatches('columns.opponent')}</TableHeader>
                  <TableHeader className="hidden w-56 lg:table-cell">
                    {tMatches('columns.competition')}
                  </TableHeader>
                  <TableHeader className="w-36">{tMatches('columns.result')}</TableHeader>
                  <TableHeader className="hidden w-20 md:table-cell">
                    {tMatches('columns.venue')}
                  </TableHeader>
                  <TableHeader className="w-24 text-right">
                    {tMatches('columns.myRating')}
                  </TableHeader>
                  <TableHeader className="hidden w-28 text-right sm:table-cell">
                    {tMatches('columns.oppRating')}
                  </TableHeader>
                  <TableHeader className="hidden w-24 text-center sm:table-cell">
                    {tMatches('continueScoring')}
                  </TableHeader>
                </tr>
              </thead>
              <tbody>
                {matches.map((match) => (
                  <InProgressMatchRow key={match.id} match={match} onDelete={onMatchDelete} />
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
